import type { NotificationItem, SystemReview } from "@scoach/types";
import { Inbox } from "@scoach/ui/icons";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";

import { isAdminEmail } from "../../lib/admins.ts";
import { useAuthStore } from "../auth/store.ts";
import { reviewsApi } from "../reviews/api.ts";
import { ReviewThread } from "../reviews/ReviewThread.tsx";
import { notificationsApi } from "./api.ts";

const POLL_MS = 60_000;

function timeAgo(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  const m = Math.floor(ms / 60_000);
  if (Number.isNaN(m) || m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * Header bell: a single inbox for BOTH shared-summary notifications AND the
 * review conversations (user ↔ admin). Users see their own reviews + admin
 * replies; admins see every user's review and can reply/edit/remove their
 * answers. The unread badge sums unread shares + unread review activity.
 */
export function NotificationBell() {
  const firebaseUid = useAuthStore((s) => s.firebaseUid);
  const user = useAuthStore((s) => s.user);
  const storeEmail = useAuthStore((s) => s.email);
  const email = user?.email ?? storeEmail ?? "";
  const admin = isAdminEmail(email);
  const viewerSide: "user" | "admin" = admin ? "admin" : "user";

  const [items, setItems] = useState<NotificationItem[]>([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [reviews, setReviews] = useState<SystemReview[]>([]);
  const [reviewUnread, setReviewUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const nav = useNavigate();

  const refresh = useCallback(() => {
    notificationsApi
      .list()
      .then((r) => {
        setItems(r.items);
        setNotifUnread(r.unread);
      })
      .catch(() => {});
    (admin ? reviewsApi.listAll() : reviewsApi.listMine())
      .then((r) => {
        setReviews(r.reviews);
        setReviewUnread(r.unread);
      })
      .catch(() => {});
  }, [admin]);

  useEffect(() => {
    if (!firebaseUid) return;
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer);
  }, [firebaseUid, refresh]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const unread = notifUnread + reviewUnread;

  function toggle() {
    const next = !open;
    setOpen(next);
    // Opening clears the SHARE unread badge (server marks all read). Review threads
    // are marked read individually when expanded.
    if (next && notifUnread > 0) {
      setNotifUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      notificationsApi.markRead().catch(() => {});
    }
  }

  function openMeeting(n: NotificationItem) {
    setOpen(false);
    nav({ to: "/meetings/$id/summary", params: { id: n.meetingId } });
  }

  function reviewUnreadFlag(r: SystemReview): boolean {
    return (admin ? r.adminUnread : r.ownerUnread) ? true : false;
  }

  function expandReview(r: SystemReview) {
    const next = expandedId === r.id ? null : r.id;
    setExpandedId(next);
    if (next && reviewUnreadFlag(r)) {
      setReviews((prev) => prev.map((x) => (x.id === r.id ? { ...x, adminUnread: 0, ownerUnread: 0 } : x)));
      setReviewUnread((u) => Math.max(0, u - 1));
      reviewsApi.markRead(r.id).catch(() => {});
    }
  }

  function onThreadChange(updated: SystemReview) {
    setReviews((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
  }

  const nothing = items.length === 0 && reviews.length === 0;

  return (
    <div className="notif-wrap" ref={ref}>
      <button
        type="button"
        className="icon-btn notif-btn"
        aria-label={unread > 0 ? `${unread} new notifications` : "Notifications"}
        title="Notifications & messages"
        onClick={toggle}
      >
        <Inbox size={18} />
        {unread > 0 && <span className="notif-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <div className="notif-pop" style={{ width: 380 }}>
          <div className="notif-pop-head">Notifications</div>
          {nothing ? (
            <div className="notif-empty">
              Nothing yet. Shared summaries and replies to your reviews show up here.
            </div>
          ) : (
            <div className="notif-list" style={{ maxHeight: 480 }}>
              {/* Review conversations */}
              {reviews.length > 0 && (
                <div className="notif-section-label">
                  {admin ? "Reviews & messages" : "Your reviews"}
                </div>
              )}
              {reviews.map((r) => (
                <div key={r.id} className={`notif-item ${reviewUnreadFlag(r) ? "unread" : ""}`}>
                  <button
                    type="button"
                    style={{ all: "unset", cursor: "pointer", display: "block", width: "100%" }}
                    onClick={() => expandReview(r)}
                  >
                    <div className="notif-item-title">
                      {"★".repeat(r.rating)}
                      {"☆".repeat(Math.max(0, 5 - r.rating))}
                      {admin ? ` · ${r.name || r.email}` : ""}
                      {reviewUnreadFlag(r) && (
                        <span style={{ color: "var(--gc-red, #d93025)", marginLeft: 6, fontSize: 11 }}>
                          ● new
                        </span>
                      )}
                    </div>
                    <div className="notif-item-sub">
                      {r.text || (r.messages?.length ? `${r.messages.length} message(s)` : "Rating only")}
                    </div>
                  </button>
                  {expandedId === r.id && (
                    <div style={{ marginTop: 8 }}>
                      <ReviewThread review={r} viewerSide={viewerSide} onChange={onThreadChange} />
                    </div>
                  )}
                </div>
              ))}

              {/* Shared-summary notifications */}
              {items.length > 0 && <div className="notif-section-label">Shared with you</div>}
              {items.map((n) => (
                <button
                  type="button"
                  key={n.id}
                  className={`notif-item ${n.read ? "" : "unread"}`}
                  onClick={() => openMeeting(n)}
                >
                  <div className="notif-item-title">
                    <strong>{n.fromName}</strong> shared a meeting summary
                  </div>
                  <div className="notif-item-sub">
                    {n.clientName ? `${n.clientName} · ` : ""}
                    {n.meetingTitle}
                  </div>
                  <div className="notif-item-time">{timeAgo(n.createdAt)}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
